import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  FlatList,
  type LayoutChangeEvent,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View
} from 'react-native'

import { Badge, Button, Screen } from '../components'
import { useBreakpoint } from '../components/useBreakpoint'
import { edgeMarginRem } from '../layout/margins'
import { lstrings, sprintf } from '../locales/strings'
import { useInsets } from '../platform/safeArea'
import { lightHaptic } from '../scoring/lightHaptic'
import { cacheStyles, type Theme, useTheme } from '../theme/ThemeContext'
import { ChatArrivalHost } from './ChatArrivalHost'
import { ChatAvatar } from './ChatAvatar'
import { conversationIdentity } from './chatIdentityModel'
import { pressPoint, pressStamp } from './chatInteractionModel'
import {
  canStartDm,
  conversationPill,
  conversationTitle,
  groupChats,
  timeLabel
} from './chatModel'
import { getChatStyles } from './chatStyles'
import { ChatViewport, useChatViewport } from './ChatViewport'
import {
  bindUserScroll,
  cancelUserScroll,
  immediatePressProps
} from './chatWebInteractions'
import {
  CHAT_WIDE_WIDTH,
  type ChatListCaptureResult,
  type ChatListPositionCapture
} from './chatWorkspaceModel'
import { ConversationMenu } from './ConversationMenu'
import type { ConversationListItem } from './engine/engine'
import type { ChatScreenEngine } from './screenEngine'

export interface ChatListScreenProps {
  engine: Pick<ChatScreenEngine, 'refresh' | 'conversations' | 'subscribe'> &
    Pick<Partial<ChatScreenEngine>, 'loadPreview' | 'setPreferences'>
  onOpen: (chat: ConversationListItem) => void
  onNewMessage: () => void
  now?: () => Date
  embedded?: boolean
  narrow?: boolean
  selectedId?: string
  /** A remembered selection on the narrow list is still offscreen. */
  selectedVisible?: boolean
  initialOffset?: number
  initialAnchor?: { id: string; relativeY: number }
  onOffset?: (offset: number) => void
  onAnchor?: (anchor: { id: string; relativeY: number } | undefined) => void
  registerPositionCapture?: (capture: ChatListPositionCapture) => () => void
}
type Row =
  | { type: 'heading'; key: string; title: string }
  | { type: 'chat'; key: string; chat: ConversationListItem }

export function ChatListScreen(props: ChatListScreenProps): React.ReactElement {
  const { engine } = props
  const { registerPositionCapture } = props
  const theme = useTheme()
  const styles = getStyles(theme)
  const shared = getChatStyles(theme)
  const viewport = useChatViewport()
  const insets = useInsets()
  const breakpoint = useBreakpoint()
  const margin = theme.rem(edgeMarginRem(theme, breakpoint))
  const gutter = {
    left: Math.max(margin, insets.left),
    right: Math.max(margin, insets.right)
  }
  const windowWidth = useWindowDimensions().width
  const narrow = props.narrow ?? windowWidth < CHAT_WIDE_WIDTH
  const [chats, setChats] = useState<ConversationListItem[]>()
  const [failed, setFailed] = useState(false)
  const [menu, setMenu] = useState<ConversationListItem>()
  const list = useRef<FlatList<Row>>(null)
  const visible = useRef<string[]>([])
  const gestureGeneration = useRef(0)
  const webInput = useRef<() => void>(() => {})
  const webPosition = useRef<(offset: number) => void>(() => {})
  const webOwner = useRef<
    | { data: string | undefined; visibility: number; generation: number }
    | undefined
  >(undefined)
  const restored = useRef(false)
  const scrollOwner = useRef({
    live: true,
    visible: true,
    onOffset: props.onOffset,
    onAnchor: props.onAnchor
  })
  const listVisible =
    props.embedded !== true || !narrow || props.selectedVisible !== true
  const visibilityState = useRef({ visible: listVisible, version: 0 })
  if (visibilityState.current.visible !== listVisible) {
    visibilityState.current.visible = listVisible
    visibilityState.current.version += 1
  }
  const visibilityVersion = visibilityState.current.version
  scrollOwner.current.visible = listVisible
  scrollOwner.current.onOffset = props.onOffset
  scrollOwner.current.onAnchor = props.onAnchor
  useEffect(() => {
    const owner = scrollOwner.current
    owner.live = true
    return () => {
      owner.live = false
    }
  }, [])
  const rows: Row[] =
    chats == null
      ? []
      : groupChats(chats).flatMap(group => [
          { type: 'heading' as const, key: group.key, title: group.title! },
          ...group.chats.map(chat => ({
            type: 'chat' as const,
            key: chat.id,
            chat
          }))
        ])
  // Row order is geometry. A same-ID recency/pin reorder invalidates work,
  // while a metadata array that retains that order does not.
  const dataKey =
    chats == null ? undefined : JSON.stringify(rows.map(row => row.key))
  const currentDataKey = useRef(dataKey)
  currentDataKey.current = dataKey
  const listGeometry = useRef({
    height: 0,
    content: 0,
    data: undefined as string | undefined
  })
  const restoreOffset = useRef(props.initialOffset ?? 0)
  const anchor = useRef(props.initialAnchor)
  const anchorOwned = useRef(props.initialAnchor != null)
  const currentOffset = useRef(props.initialOffset ?? 0)
  const userPosition = useRef<
    | {
        native: boolean
        moved: boolean
        data: string | undefined
        visibility: number
        generation: number
      }
    | undefined
  >(undefined)
  const pendingMomentum = useRef<
    | {
        data: string | undefined
        visibility: number
        generation: number
        moved: boolean
      }
    | undefined
  >(undefined)
  const explicitTop = useRef(
    (props.initialOffset ?? 0) === 0 && props.initialAnchor == null
  )
  const acknowledgedOffset = useRef<
    | {
        offset: number
        data: string | undefined
        visibility: number
        generation: number
      }
    | undefined
  >(undefined)
  const [fastRestore, setFastRestore] = useState(props.initialAnchor != null)
  const requested = useRef<number | undefined>(undefined)
  const requestedFinal = useRef(false)
  const requestedVisibility = useRef(visibilityVersion)
  const requestedVersion = useRef(0)
  const rejectedRequest = useRef(false)
  const rowRefs = useRef(new Map<string, React.ComponentRef<typeof View>>())
  const positions = useRef(
    new Map<
      string,
      { y: number; height: number; data: string | undefined; version: number }
    >()
  )
  const measuredTail = useRef<
    { data: string | undefined; end: number; version: number } | undefined
  >(undefined)
  const layoutVersion = useRef(0)
  const revealProgress = useRef({
    data: undefined as string | undefined,
    offset: -1
  })
  const restoration = useRef<() => void>(() => {})
  const measureRows = useRef<(id: string) => void>(() => {})
  const measuring = useRef(false)
  const dirty = useRef(false)
  const seeking = useRef<
    { data: string | undefined; index: number; version: number } | undefined
  >(undefined)
  const validatingGeometry = useRef(false)
  const geometryDirty = useRef(false)
  const geometryValidation = useRef<() => void>(() => {})
  const liveData = (): boolean =>
    scrollOwner.current.live &&
    scrollOwner.current.visible &&
    dataKey === currentDataKey.current &&
    visibilityVersion === visibilityState.current.version
  const rowPosition = async (
    id: string,
    retry = false,
    cancelled: () => boolean = () => !liveData()
  ): Promise<{ y: number; height: number } | undefined> => {
    const deadline = Date.now() + 2000
    while (!cancelled()) {
      const position = await new Promise<
        { y: number; height: number } | undefined
      >(resolve => {
        const row = rowRefs.current.get(id)
        const content = list.current?.getScrollResponder()?.getInnerViewRef()
        if (row == null || content == null) {
          resolve(undefined)
          return
        }
        const timeout = setTimeout(() => {
          resolve(undefined)
        }, 100)
        row.measureLayout(
          content,
          (_x, y, width, height) => {
            clearTimeout(timeout)
            resolve(
              Number.isFinite(y) && width > 0 && height > 0
                ? { y, height }
                : undefined
            )
          },
          () => {
            clearTimeout(timeout)
            resolve(undefined)
          }
        )
      })
      if (position != null || !retry || Date.now() >= deadline) return position
      await new Promise<void>(resolve => setTimeout(resolve, 25))
    }
    return undefined
  }

  const savePosition = (): void => {
    if (!liveData() || !restored.current) return
    const offset = currentOffset.current
    if (explicitTop.current) {
      scrollOwner.current.onOffset?.(0)
      scrollOwner.current.onAnchor?.(undefined)
      return
    }
    scrollOwner.current.onOffset?.(offset)
    const candidates = [...positions.current].filter(
      ([, p]) =>
        p.data === dataKey &&
        p.version === layoutVersion.current &&
        p.y + p.height > offset &&
        p.y < offset + listGeometry.current.height
    )
    candidates.sort((a, b) => a[1].y - b[1].y)
    const first = candidates[0]
    if (first != null)
      scrollOwner.current.onAnchor?.({
        id: first[0],
        relativeY: first[1].y - offset
      })
  }
  const navigationCapture = useRef<() => Promise<ChatListCaptureResult>>(
    async () => 'unavailable'
  )
  const captureSequence = useRef(0)
  navigationCapture.current = async () => {
    if (!liveData() || !restored.current) return 'unavailable'
    const sequence = ++captureSequence.current
    const version = layoutVersion.current
    const generation = gestureGeneration.current
    const rawOffset = currentOffset.current
    const responder = list.current?.getScrollResponder()
    const content = responder?.getInnerViewRef()
    const viewport = responder?.getNativeScrollRef()
    const currentOwner = (): boolean =>
      liveData() &&
      sequence === captureSequence.current &&
      generation === gestureGeneration.current &&
      rawOffset === currentOffset.current &&
      content === list.current?.getScrollResponder()?.getInnerViewRef() &&
      viewport === list.current?.getScrollResponder()?.getNativeScrollRef()
    const current = (): boolean =>
      currentOwner() && version === layoutVersion.current
    const superseded = (): ChatListCaptureResult =>
      currentOwner() ? 'stale' : 'unavailable'
    interface Bounds {
      y: number
      width: number
      height: number
    }
    const measure = async (
      node:
        | {
            measureInWindow: (
              callback: (
                x: number,
                y: number,
                width: number,
                height: number
              ) => void
            ) => void
          }
        | null
        | undefined
    ): Promise<Bounds | undefined> =>
      await new Promise(resolve => {
        if (node == null || typeof node.measureInWindow !== 'function') {
          resolve(undefined)
          return
        }
        // A failed measurement falls back; elapsed time never establishes position.
        const timeout = setTimeout(() => {
          resolve(undefined)
        }, 100)
        node.measureInWindow((_x, y, width, height) => {
          clearTimeout(timeout)
          resolve(
            Number.isFinite(y) &&
              Number.isFinite(width) &&
              Number.isFinite(height) &&
              width > 0 &&
              height > 0
              ? { y, width, height }
              : undefined
          )
        })
      })
    const [contentBounds, viewportBounds] = await Promise.all([
      measure(content),
      measure(viewport)
    ])
    if (contentBounds == null || viewportBounds == null) return 'unavailable'
    if (!current()) return superseded()
    const offset = Math.max(0, viewportBounds.y - contentBounds.y)
    const atChosenTop = explicitTop.current && offset === 0
    let saved: { id: string; relativeY: number } | undefined
    if (!atChosenTop) {
      const measured = await Promise.all(
        [...rowRefs.current].map(async ([id, row]) => ({
          id,
          row,
          bounds: await measure(row)
        }))
      )
      if (!current()) return superseded()
      const visibleRows = measured.filter(
        value =>
          value.bounds != null &&
          rowRefs.current.get(value.id) === value.row &&
          value.bounds.y + value.bounds.height > viewportBounds.y &&
          value.bounds.y < viewportBounds.y + viewportBounds.height
      )
      visibleRows.sort((a, b) => a.bounds!.y - b.bounds!.y)
      const first = visibleRows[0]
      if (first == null) return 'unavailable'
      saved = { id: first.id, relativeY: first.bounds!.y - viewportBounds.y }
    }
    if (!current()) return superseded()
    currentOffset.current = offset
    restoreOffset.current = offset
    if (offset > 0) explicitTop.current = false
    anchor.current = saved
    anchorOwned.current = false
    scrollOwner.current.onOffset?.(offset)
    scrollOwner.current.onAnchor?.(saved)
    return 'accepted'
  }
  useEffect(() => {
    if (!listVisible) return
    const stop = registerPositionCapture?.(async () => {
      return await navigationCapture.current()
    })
    return () => {
      captureSequence.current += 1
      stop?.()
    }
  }, [listVisible, registerPositionCapture])

  const measureRow = (id: string): void => {
    const version = layoutVersion.current
    const generation = gestureGeneration.current
    rowPosition(id)
      .then(position => {
        if (
          !liveData() ||
          version !== layoutVersion.current ||
          generation !== gestureGeneration.current ||
          position == null
        )
          return
        positions.current.set(id, { ...position, data: dataKey, version })
        if (id === rows.at(-1)?.key)
          measuredTail.current = {
            data: dataKey,
            end: position.y + position.height,
            version
          }
        savePosition()
        restoration.current()
      })
      .catch(() => undefined)
  }
  measureRows.current = measureRow
  const completeContent = (): boolean => {
    const geometry = listGeometry.current
    const tail = measuredTail.current
    return (
      geometry.data === dataKey &&
      (chats?.length === 0 ||
        (tail != null &&
          tail.data === dataKey &&
          tail.version === layoutVersion.current &&
          Math.abs(tail.end + viewport.contentBottom - geometry.content) <= 1))
    )
  }
  const verifyZeroDistance = (offset: number, final: boolean): boolean => {
    const geometry = listGeometry.current
    // Complete current content shorter than the actual viewport has only one
    // reachable normalized offset. A widget already at zero emits no event.
    return (
      final &&
      offset === 0 &&
      liveData() &&
      geometry.data === dataKey &&
      geometry.height > 0 &&
      geometry.content > 0 &&
      geometry.content <= geometry.height &&
      completeContent()
    )
  }
  const finishRestoration = (offset: number): void => {
    currentOffset.current = offset
    restoreOffset.current = offset
    const saved = anchor.current
    const position = saved == null ? undefined : positions.current.get(saved.id)
    if (
      saved != null &&
      position != null &&
      position.data === dataKey &&
      position.version === layoutVersion.current
    )
      anchor.current = { id: saved.id, relativeY: position.y - offset }
    restored.current = true
    setFastRestore(false)
    savePosition()
  }
  const requestOffset = (offset: number, final = false): void => {
    if (
      requested.current === offset &&
      requestedVisibility.current === visibilityVersion &&
      !(
        rejectedRequest.current &&
        requestedVersion.current !== layoutVersion.current
      )
    ) {
      // A reveal already reached this same numeric destination. Validate its
      // final meaning without issuing a duplicate scroll in this visible pane.
      requestedFinal.current = final
      requestedVersion.current = layoutVersion.current
      const acknowledged = acknowledgedOffset.current
      if (
        final &&
        liveData() &&
        ((acknowledged != null &&
          acknowledged.data === dataKey &&
          acknowledged.visibility === visibilityVersion &&
          acknowledged.generation === gestureGeneration.current &&
          Math.abs(acknowledged.offset - offset) <= 1) ||
          verifyZeroDistance(offset, final))
      )
        finishRestoration(offset)
      return
    }
    requested.current = offset
    requestedFinal.current = final
    requestedVisibility.current = visibilityVersion
    requestedVersion.current = layoutVersion.current
    rejectedRequest.current = false
    if (final) restored.current = false
    userPosition.current = undefined
    pendingMomentum.current = undefined
    webOwner.current = undefined
    cancelUserScroll(list.current)
    list.current?.scrollToOffset({ offset, animated: false })
    if (verifyZeroDistance(offset, final)) finishRestoration(offset)
  }
  const seekAnchor = (index: number): void => {
    const previous = seeking.current
    if (
      previous != null &&
      previous.data === dataKey &&
      previous.index === index &&
      previous.version === layoutVersion.current
    )
      return
    restored.current = false
    setFastRestore(true)
    requestedFinal.current = false
    seeking.current = { data: dataKey, index, version: layoutVersion.current }
    userPosition.current = undefined
    pendingMomentum.current = undefined
    webOwner.current = undefined
    cancelUserScroll(list.current)
    list.current?.scrollToIndex({ index, animated: false, viewPosition: 0.5 })
  }
  const revalidateGeometry = (): void => {
    if (
      !liveData() ||
      dataKey == null ||
      listGeometry.current.data == null ||
      listGeometry.current.data === dataKey
    )
      return
    if (validatingGeometry.current) {
      geometryDirty.current = true
      return
    }
    validatingGeometry.current = true
    geometryDirty.current = false
    const deadline = Date.now() + 2000
    const generation = gestureGeneration.current
    const validate = async (): Promise<void> => {
      while (
        liveData() &&
        generation === gestureGeneration.current &&
        listGeometry.current.data !== dataKey &&
        Date.now() < deadline
      ) {
        const version = layoutVersion.current
        const size = await new Promise<
          { width: number; height: number } | undefined
        >(resolve => {
          const content = list.current?.getScrollResponder()?.getInnerViewRef()
          if (content == null) {
            resolve(undefined)
            return
          }
          const timer = setTimeout(() => {
            resolve(undefined)
          }, 100)
          content.measure((_x, _y, width, height) => {
            clearTimeout(timer)
            resolve(width > 0 && height > 0 ? { width, height } : undefined)
          })
        })
        if (!liveData() || generation !== gestureGeneration.current) return
        if (size != null && version === layoutVersion.current) {
          listGeometry.current.content = size.height
          listGeometry.current.data = dataKey
          restoration.current()
          for (const id of visible.current) measureRows.current(id)
          return
        }
        await new Promise<void>(resolve => setTimeout(resolve, 25))
      }
    }
    validate()
      .catch(() => undefined)
      .finally(() => {
        validatingGeometry.current = false
        if (
          geometryDirty.current &&
          scrollOwner.current.live &&
          scrollOwner.current.visible
        ) {
          geometryDirty.current = false
          geometryValidation.current()
        }
      })
  }
  geometryValidation.current = revalidateGeometry
  const restoreList = (): void => {
    if (
      !liveData() ||
      chats == null ||
      (restored.current && !anchorOwned.current)
    )
      return
    const { height, content, data } = listGeometry.current
    if (height <= 0 || content <= 0 || data !== dataKey) return
    const saved = anchor.current
    const savedIndex =
      saved == null ? -1 : rows.findIndex(row => row.key === saved.id)
    if (savedIndex >= 0 && !rowRefs.current.has(saved!.id)) {
      seekAnchor(savedIndex)
      return
    }
    if (
      saved != null &&
      rows.some(row => row.key === saved.id) &&
      rowRefs.current.has(saved.id)
    ) {
      if (measuring.current) {
        dirty.current = true
        return
      }
      measuring.current = true
      dirty.current = false
      const version = layoutVersion.current
      const generation = gestureGeneration.current
      rowPosition(
        saved.id,
        true,
        () =>
          !liveData() ||
          version !== layoutVersion.current ||
          generation !== gestureGeneration.current ||
          !anchorOwned.current
      )
        .then(position => {
          if (
            !liveData() ||
            generation !== gestureGeneration.current ||
            anchor.current !== saved ||
            !anchorOwned.current ||
            version !== layoutVersion.current ||
            dirty.current
          )
            return
          if (position == null) {
            if (listGeometry.current.content >= height + restoreOffset.current)
              requestOffset(restoreOffset.current, true)
            return
          }
          const target = Math.max(0, position.y - saved.relativeY)
          restoreOffset.current = target
          positions.current.set(saved.id, {
            ...position,
            data: dataKey,
            version
          })
          // A mounted tail is not proof that its preceding content callback
          // has measured the same complete content coordinates.
          if (target > Math.max(0, listGeometry.current.content - height)) {
            const available = Math.max(0, listGeometry.current.content - height)
            requestOffset(available, completeContent())
            return
          }
          requestOffset(target, true)
        })
        .catch(() => undefined)
        .finally(() => {
          measuring.current = false
          if (dirty.current && liveData()) {
            dirty.current = false
            restoration.current()
          }
        })
    }
    const complete = completeContent()
    if (
      content < height + restoreOffset.current &&
      chats.length > 0 &&
      !complete
    ) {
      const available = Math.max(0, content - height)
      const progress = revealProgress.current
      if (progress.data !== dataKey || available > progress.offset) {
        revealProgress.current = { data: dataKey, offset: available }
        requestOffset(available)
      }
      return
    }
    if (saved != null && rows.some(row => row.key === saved.id)) {
      if (!rowRefs.current.has(saved.id))
        requestOffset(
          Math.min(restoreOffset.current, Math.max(0, content - height))
        )
      return
    }
    anchorOwned.current = false
    anchor.current = undefined
    requestOffset(
      Math.min(restoreOffset.current, Math.max(0, content - height)),
      true
    )
  }
  restoration.current = restoreList
  const userScrolled = (native = false): void => {
    if (!scrollOwner.current.live || !scrollOwner.current.visible) return
    restored.current = true
    setFastRestore(false)
    anchorOwned.current = false
    anchor.current = undefined
    gestureGeneration.current += 1
    userPosition.current = native
      ? {
          native,
          moved: false,
          data: dataKey,
          visibility: visibilityVersion,
          generation: gestureGeneration.current
        }
      : undefined
    pendingMomentum.current = undefined
  }
  const rememberOffset = (
    event: {
      nativeEvent: { contentOffset: { y: number }; velocity?: { y: number } }
    },
    end?: 'drag' | 'momentum' | 'web'
  ): void => {
    if (!liveData()) return
    const offset = Math.max(0, event.nativeEvent.contentOffset.y)
    const active = userPosition.current
    const gesture =
      active != null &&
      active.data === dataKey &&
      active.visibility === visibilityVersion &&
      active.generation === gestureGeneration.current
        ? active
        : undefined
    if (active != null && gesture == null) {
      userPosition.current = undefined
      pendingMomentum.current = undefined
    }
    if (gesture != null) {
      gesture.moved ||= offset !== currentOffset.current
      // A delivered movement can choose the absolute top. A native drag at
      // the same normalized boundary does not replace its row anchor.
      if (gesture.moved) {
        explicitTop.current = offset === 0
        if (explicitTop.current) {
          anchor.current = undefined
          anchorOwned.current = false
        }
      }
    }
    currentOffset.current = offset
    if (end != null) {
      // Release velocity binds the following native momentum to this drag.
      // Missing velocity never prevents recording the actual terminal offset.
      const velocity = event.nativeEvent.velocity?.y
      pendingMomentum.current =
        end === 'drag' &&
        gesture?.native === true &&
        velocity != null &&
        Number.isFinite(velocity) &&
        velocity !== 0
          ? {
              data: dataKey,
              visibility: visibilityVersion,
              generation: gestureGeneration.current,
              moved: gesture.moved
            }
          : undefined
      userPosition.current = undefined
    }
    acknowledgedOffset.current = {
      offset,
      data: dataKey,
      visibility: visibilityVersion,
      generation: gestureGeneration.current
    }
    if (
      !restored.current &&
      requestedFinal.current &&
      requestedVersion.current === layoutVersion.current &&
      requested.current != null &&
      Math.abs(offset - requested.current) <= 1
    ) {
      finishRestoration(offset)
    }
    if (
      !restored.current &&
      requestedFinal.current &&
      requested.current != null &&
      Math.abs(offset - requested.current) > 1
    )
      rejectedRequest.current = true
    savePosition()
    if (!restored.current || anchorOwned.current) restoration.current()
    for (const id of visible.current) measureRow(id)
  }
  webInput.current = () => {
    userScrolled()
    webOwner.current = {
      data: dataKey,
      visibility: visibilityVersion,
      generation: gestureGeneration.current
    }
  }
  webPosition.current = offset => {
    const owner = webOwner.current
    if (
      !liveData() ||
      owner == null ||
      owner.data !== dataKey ||
      owner.visibility !== visibilityVersion ||
      owner.generation !== gestureGeneration.current
    )
      return
    // The DOM adapter delivers only actual movement from a current input.
    // Classify it inside this callback, without sharing dispatch lifetime.
    userPosition.current = {
      native: false,
      moved: true,
      data: dataKey,
      visibility: visibilityVersion,
      generation: gestureGeneration.current
    }
    rememberOffset({ nativeEvent: { contentOffset: { y: offset } } }, 'web')
  }
  useEffect(() => {
    userPosition.current = undefined
    pendingMomentum.current = undefined
    webOwner.current = undefined
    cancelUserScroll(list.current)
  }, [dataKey, listVisible])
  useEffect(() => {
    geometryValidation.current()
    restoration.current()
  }, [chats, dataKey, listVisible])
  useEffect(
    () =>
      bindUserScroll(
        list.current,
        () => {
          webInput.current()
        },
        offset => {
          webPosition.current(offset)
        }
      ),
    []
  )
  const viewability = useRef({ itemVisiblePercentThreshold: 1 }).current
  const previews = useCallback(
    (ids: string[], retry = false): void => {
      for (const id of ids)
        engine.loadPreview?.(id, retry).catch(() => undefined)
    },
    [engine]
  )
  const onVisible = useCallback(
    ({ viewableItems }: { viewableItems: Array<{ item: Row }> }): void => {
      visible.current = viewableItems.flatMap(token =>
        token.item.type === 'chat' ? [token.item.chat.id] : []
      )
      previews(visible.current)
      for (const id of visible.current) measureRows.current(id)
    },
    [previews]
  )

  useEffect(() => {
    let live = true
    const unsubscribe = engine.subscribe(event => {
      if (!live) return
      if (event.type === 'conversations') setChats(engine.conversations())
    })
    engine
      .refresh()
      .then(next => {
        if (live) {
          setChats(next)
          setFailed(false)
        }
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
      unsubscribe()
    }
  }, [engine, props.selectedId, props.selectedVisible])
  useEffect(() => {
    previews(visible.current)
  }, [chats, previews])
  const target =
    menu == null ? undefined : chats?.find(chat => chat.id === menu.id)
  const now = props.now?.() ?? new Date()
  const content = (
    <ChatViewport viewport={viewport}>
      {props.embedded !== true && (
        <ChatArrivalHost
          engine={engine}
          selectedId={props.selectedId}
          selectedVisible={props.selectedVisible}
        />
      )}
      <FlatList
        ref={list}
        testID="chat-conversation-list"
        data={rows}
        contentContainerStyle={{
          paddingTop: viewport.contentTop,
          paddingBottom: viewport.contentBottom
        }}
        keyExtractor={row => row.key}
        viewabilityConfig={viewability}
        onViewableItemsChanged={onVisible}
        onLayout={event => {
          if (!liveData()) return
          layoutVersion.current += 1
          listGeometry.current.height = event.nativeEvent.layout.height
          for (const id of visible.current) measureRows.current(id)
          revalidateGeometry()
          restoreList()
          const last = rows.at(-1)?.key
          if (last != null) measureRow(last)
        }}
        onContentSizeChange={(_width, height) => {
          if (!liveData()) return
          layoutVersion.current += 1
          listGeometry.current.content = height
          listGeometry.current.data = dataKey
          restoreList()
          for (const id of visible.current) measureRows.current(id)
          const last = rows.at(-1)?.key
          if (last != null) measureRow(last)
        }}
        onScrollToIndexFailed={info => {
          const seek = seeking.current
          if (
            !liveData() ||
            seek == null ||
            seek.data !== dataKey ||
            seek.index !== info.index ||
            !anchorOwned.current
          )
            return
          const available = Math.max(
            0,
            listGeometry.current.content - listGeometry.current.height
          )
          const estimated =
            info.averageItemLength > 0
              ? info.averageItemLength * info.index
              : available
          requestOffset(Math.min(available, estimated))
        }}
        // Keep the normal virtual window, but fill it in larger bounded
        // batches while a browser return seeks its already saved reader.
        maxToRenderPerBatch={
          Platform.OS === 'web' && fastRestore ? 50 : undefined
        }
        // Web batches remain bounded; queue them directly for the interaction
        // scheduler instead of adding a timer between restoration steps.
        updateCellsBatchingPeriod={Platform.OS === 'web' ? 0 : undefined}
        onScroll={rememberOffset}
        onScrollEndDrag={event => {
          rememberOffset(event, 'drag')
        }}
        onMomentumScrollEnd={event => {
          rememberOffset(event, 'momentum')
        }}
        onMomentumScrollBegin={event => {
          if (!liveData()) return
          const preceding = pendingMomentum.current
          pendingMomentum.current = undefined
          if (
            Platform.OS !== 'web' &&
            preceding != null &&
            preceding.data === dataKey &&
            preceding.visibility === visibilityVersion &&
            preceding.generation === gestureGeneration.current
          ) {
            userPosition.current = {
              native: true,
              moved: preceding.moved,
              data: dataKey,
              visibility: visibilityVersion,
              generation: gestureGeneration.current
            }
            return
          }
          if (preceding != null) return
          const offset = event?.nativeEvent?.contentOffset?.y
          if (
            Platform.OS === 'android' &&
            restored.current &&
            offset != null &&
            Number.isFinite(offset)
          ) {
            // Android hardware-wheel input starts momentum without a drag.
            // Its scroll coordinate can arrive before this lifecycle event.
            const moved =
              Math.max(0, offset) !== currentOffset.current ||
              (explicitTop.current && offset > 0)
            userScrolled(true)
            if (userPosition.current != null) userPosition.current.moved = moved
            rememberOffset(event)
          }
        }}
        scrollEventThrottle={100}
        onScrollBeginDrag={() => {
          // RNW also emits this for the programmatic restoration steps.
          if (Platform.OS !== 'web' && liveData()) {
            userScrolled(true)
          }
        }}
        keyboardShouldPersistTaps="handled"
        ListHeaderComponent={
          <View
            style={[
              styles.actions,
              { paddingLeft: gutter.left, paddingRight: gutter.right }
            ]}
          >
            {chats != null && canStartDm(chats) && (
              <Button
                label={lstrings.chat_new_message}
                kind="primary"
                icon="edit"
                compact
                onPress={props.onNewMessage}
              />
            )}
          </View>
        }
        ListEmptyComponent={
          <View
            style={[
              styles.empty,
              { paddingLeft: gutter.left, paddingRight: gutter.right }
            ]}
          >
            {chats != null && !failed && (
              <Text style={shared.heading}>{lstrings.chat_empty}</Text>
            )}
            <Text style={failed ? shared.error : shared.muted}>
              {failed
                ? lstrings.chat_cannot_load
                : chats == null
                  ? lstrings.common_loading
                  : lstrings.chat_empty_detail}
            </Text>
          </View>
        }
        renderItem={({ item, index }) =>
          item.type === 'heading' ? (
            <View
              style={[
                styles.heading,
                { paddingLeft: gutter.left, paddingRight: gutter.right }
              ]}
            >
              <Text style={shared.heading}>{item.title}</Text>
            </View>
          ) : (
            <ConversationRow
              chat={item.chat}
              gutter={gutter}
              separator={rows[index + 1]?.type === 'chat'}
              onRef={node => {
                if (node == null) rowRefs.current.delete(item.chat.id)
                else rowRefs.current.set(item.chat.id, node)
              }}
              onLayout={event => {
                if (!liveData()) return
                layoutVersion.current += 1
                measureRow(item.chat.id)
                for (const id of visible.current) measureRows.current(id)
                revalidateGeometry()
                const last = rows.at(-1)?.key
                if (last != null && last !== item.chat.id) measureRow(last)
                restoreList()
              }}
              onRetryPreview={
                engine.loadPreview == null
                  ? undefined
                  : () => {
                      previews([item.chat.id], true)
                    }
              }
              now={now}
              selected={item.chat.id === props.selectedId}
              narrow={narrow}
              gestureGeneration={gestureGeneration}
              onOpen={() => {
                props.onOpen(item.chat)
              }}
              onMenu={() => {
                setMenu(item.chat)
              }}
            />
          )
        }
      />
      {target != null && (
        <ConversationMenu
          conversation={target}
          engine={engine}
          onClose={() => {
            setMenu(undefined)
          }}
        />
      )}
    </ChatViewport>
  )
  return props.embedded === true ? (
    content
  ) : (
    <Screen
      overlayContent
      wide
      scroll={false}
      contentStyle={[styles.fill, { paddingHorizontal: 0, paddingVertical: 0 }]}
    >
      {content}
    </Screen>
  )
}

function ConversationRow(props: {
  chat: ConversationListItem
  gutter: { left: number; right: number }
  separator: boolean
  now: Date
  selected: boolean
  narrow: boolean
  gestureGeneration: React.RefObject<number>
  onOpen: () => void
  onMenu: () => void
  onRetryPreview?: () => void
  onLayout?: (event: LayoutChangeEvent) => void
  onRef?: (node: React.ComponentRef<typeof View> | null) => void
}): React.ReactElement {
  const { chat } = props
  const theme = useTheme()
  const styles = getStyles(theme)
  const shared = getChatStyles(theme)
  const ref = useRef<React.ComponentRef<typeof View>>(null)
  const held = useRef({
    x: 0,
    y: 0,
    generation: 0,
    cancelled: false,
    recognized: false,
    pointer: false,
    nativeStart: undefined as ReturnType<typeof pressStamp>
  })
  const title = conversationTitle(chat)
  const pill = conversationPill(chat)
  const unread = chat.unread > 0 || chat.preferences?.manualUnread === true
  const preview = chat.locked
    ? lstrings.chat_locked_message
    : chat.previewFailed === true
      ? lstrings.chat_latest_failed
      : (chat.preview ??
        (chat.lastSeq === 0 || chat.lastPostSeq === 0
          ? ''
          : lstrings.chat_latest_loading))
  const unreadLabel = !unread
    ? undefined
    : chat.unread > 0
      ? sprintf(lstrings.chat_unread_label, chat.unread)
      : lstrings.chat_manual_unread_label
  const openMenu = (): void => {
    if (
      held.current.cancelled ||
      held.current.recognized ||
      !held.current.pointer ||
      held.current.generation !== props.gestureGeneration.current
    )
      return
    held.current.recognized = true
    lightHaptic(ref.current)
    props.onMenu()
  }
  return (
    <View
      ref={node => {
        ref.current = node
        props.onRef?.(node)
      }}
      collapsable={false}
      onLayout={props.onLayout}
      style={[
        styles.row,
        {
          paddingLeft: props.gutter.left,
          paddingRight: props.gutter.right
        },
        props.selected && styles.selected
      ]}
    >
      <Pressable
        testID={'chat-conversation-' + chat.id}
        {...immediatePressProps}
        style={styles.openRow}
        accessibilityRole="button"
        // The row is one button to screen readers: name what it shows.
        accessibilityLabel={[chat.title, preview, unreadLabel]
          .filter(part => part != null && part !== '')
          .join(', ')}
        accessibilityHint={pill}
        accessibilityActions={[
          { name: 'longpress', label: lstrings.chat_conversation_actions }
        ]}
        onAccessibilityAction={event => {
          if (event.nativeEvent.actionName === 'longpress') props.onMenu()
        }}
        onPressIn={event => {
          const point = pressPoint(event.nativeEvent)
          const nativeStart = pressStamp(event.nativeEvent)
          if (
            nativeStart != null &&
            nativeStart.identifier === held.current.nativeStart?.identifier &&
            nativeStart.timestamp === held.current.nativeStart?.timestamp
          )
            return
          held.current = {
            x: point?.x ?? 0,
            y: point?.y ?? 0,
            generation: props.gestureGeneration.current,
            cancelled: false,
            recognized: false,
            pointer: point != null,
            nativeStart
          }
        }}
        onTouchMove={event => {
          const point = pressPoint(event.nativeEvent)
          if (
            point == null ||
            Math.hypot(point.x - held.current.x, point.y - held.current.y) > 10
          )
            held.current.cancelled = true
        }}
        onTouchCancel={() => {
          held.current.cancelled = true
        }}
        onLongPress={props.narrow ? openMenu : undefined}
        delayLongPress={500}
        onPress={event => {
          const started = held.current.nativeStart
          const released = pressStamp(event.nativeEvent)
          if (props.narrow && started != null && released != null) {
            const point = pressPoint(event.nativeEvent)
            if (
              released.identifier !== started.identifier ||
              released.timestamp < started.timestamp ||
              point == null ||
              Math.hypot(point.x - held.current.x, point.y - held.current.y) >
                10
            )
              held.current.cancelled = true
            else if (released.timestamp - started.timestamp >= 500) openMenu()
          }
          if (
            !held.current.recognized &&
            !held.current.cancelled &&
            (!held.current.pointer ||
              held.current.generation === props.gestureGeneration.current)
          )
            props.onOpen()
        }}
      >
        <ChatAvatar identity={conversationIdentity(chat)} />
        <View style={styles.text}>
          <View style={[styles.line, styles.titleLine]}>
            <Text
              style={[styles.title, unread && styles.titleUnread]}
              numberOfLines={1}
            >
              {title}
            </Text>
            {pill != null && (
              <View style={styles.pill}>
                <Text style={styles.pillText}>{pill}</Text>
              </View>
            )}
          </View>
          <View style={styles.line}>
            <Text style={[shared.small, styles.preview]} numberOfLines={2}>
              {preview}
            </Text>
            <View style={styles.trailing}>
              <Text style={shared.small}>
                {timeLabel(
                  chat.lastPostAt === undefined
                    ? chat.lastMessageAt
                    : chat.lastPostAt,
                  props.now
                )}
              </Text>
              {unread && (
                <View style={styles.unread} accessibilityLabel={unreadLabel}>
                  <Text style={styles.unreadText}>
                    {chat.unread > 0 ? chat.unread : '•'}
                  </Text>
                </View>
              )}
            </View>
          </View>
          <View style={styles.badges}>
            {chat.locked && (
              <Badge label={lstrings.chat_locked_badge} tone="neutral" />
            )}
            {chat.closed && (
              <Badge label={lstrings.chat_closed_badge} tone="neutral" />
            )}
            {chat.preferences?.muted === true && (
              <Badge label={lstrings.chat_muted} tone="neutral" />
            )}
          </View>
        </View>
      </Pressable>
      {chat.previewFailed === true &&
        !chat.locked &&
        props.onRetryPreview != null && (
          <Pressable
            testID={'chat-preview-retry-' + chat.id}
            accessibilityRole="button"
            accessibilityLabel={
              chat.previewRetrying === true
                ? lstrings.common_loading
                : lstrings.chat_preview_retry
            }
            disabled={chat.previewRetrying === true}
            onPressIn={event => {
              event.stopPropagation()
            }}
            onPress={event => {
              event.stopPropagation()
              props.onRetryPreview?.()
            }}
            style={{
              alignSelf: 'flex-start',
              paddingVertical: theme.rem(0.25)
            }}
          >
            <Text style={[shared.small, { color: theme.activeColor }]}>
              {chat.previewRetrying === true
                ? lstrings.common_loading
                : lstrings.common_retry}
            </Text>
          </Pressable>
        )}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          lstrings.chat_conversation_actions + ': ' + chat.title
        }
        onPress={props.onMenu}
        style={styles.more}
      >
        <View accessible={false} style={styles.ellipsis}>
          {[0, 1, 2].map(dot => (
            <View key={dot} style={styles.ellipsisDot} />
          ))}
        </View>
      </Pressable>
      {props.separator && (
        <View
          testID="chat-row-separator"
          style={[
            styles.separator,
            {
              left: props.gutter.left + theme.rem(3.125),
              right: props.gutter.right
            }
          ]}
        />
      )}
    </View>
  )
}

const getStyles = cacheStyles((theme: Theme) => ({
  fill: { flex: 1, minHeight: 0 },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.rem(0.5),
    padding: theme.rem(0.75)
  },
  heading: {
    paddingHorizontal: theme.rem(1),
    paddingTop: theme.rem(1),
    paddingBottom: theme.rem(0.5)
  },
  empty: { padding: theme.rem(1) },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: theme.rem(4.5)
  },
  selected: { backgroundColor: theme.containerBackgroundColor },
  openRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.rem(0.625),
    paddingVertical: theme.rem(0.75)
  },
  text: { flex: 1, minWidth: 0, gap: theme.rem(0.25) },
  line: { flexDirection: 'row', alignItems: 'center', gap: theme.rem(0.375) },
  titleLine: { flexWrap: 'wrap' },
  title: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: theme.rem(6),
    minWidth: theme.rem(6),
    color: theme.primaryTextColor,
    fontFamily: theme.semiBoldTextFont,
    fontSize: theme.rem(0.9375)
  },
  titleUnread: { fontFamily: theme.boldTextFont, fontWeight: '700' },
  pill: {
    flexShrink: 0,
    borderRadius: theme.rem(2),
    backgroundColor: theme.pillBackground,
    paddingHorizontal: theme.rem(0.375),
    paddingVertical: theme.rem(0.125)
  },
  pillText: {
    color: theme.pillTextColor,
    fontFamily: theme.semiBoldTextFont,
    fontSize: theme.rem(0.75)
  },
  preview: { flex: 1 },
  trailing: {
    alignItems: 'flex-end',
    minWidth: theme.rem(2.5),
    gap: theme.rem(0.25)
  },
  badges: { flexDirection: 'row', gap: theme.rem(0.25) },
  unread: {
    minWidth: theme.rem(1.25),
    height: theme.rem(1.25),
    borderRadius: theme.rem(0.625),
    backgroundColor: theme.primaryButtonColor,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.rem(0.375)
  },
  unreadText: {
    color: theme.buttonTextColor,
    fontFamily: theme.boldTextFont,
    fontSize: theme.rem(0.75)
  },
  more: {
    minWidth: theme.rem(2.75),
    minHeight: theme.rem(2.75),
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  ellipsis: { gap: theme.rem(0.1875), alignItems: 'center' },
  ellipsisDot: {
    width: theme.rem(0.1875),
    height: theme.rem(0.1875),
    borderRadius: theme.rem(0.09375),
    backgroundColor: theme.secondaryTextColor
  },
  separator: {
    position: 'absolute',
    bottom: 0,
    height: 1,
    backgroundColor: theme.defaultBorderColor
  },
  announcement: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden'
  }
}))
